/**
 * Tech 主题布局组件
 * 六边形 Logo、图标导航、磨砂玻璃 Header、网格背景
 */
import React, { Suspense, lazy, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Dropdown } from 'antd'
import { PoweroffOutlined, DownOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks'
import { useLanguage } from '@/hooks/useLanguage'
import { useTheme } from '@/hooks/useTheme'
import type { MenuProps } from 'antd'
import BreadcrumbNav from '@/components/common/Breadcrumb'
import TechTopMenu from './TechTopMenu'
import LanguageSwitcher from '@/components/common/LanguageSwitcher'
import FullscreenToggle from '@/components/common/FullscreenToggle'
import ThemeSwitcher from '@/components/common/ThemeSwitcher'
import UiScaleControl from '@/components/common/UiScaleControl'
import { getLocalizedUserDisplayName } from '@/utils/userDisplay'
import styles from './TechLayout.module.less'
import { useMapUiStore } from '@/store/mapUiStore'
import { useUiScale, PANEL_SELF_SCALE_CSS_VAR } from '@/hooks/useUiScale'

const { Header, Content } = Layout
const APP_VERSION = 'v1.0'
const FULL_BLEED_CONTENT_PATHS = new Set(['/', '/disease', '/patrol', '/draw', '/data-dashboard'])
const VERSION_DOCK_HIDDEN_PATHS = new Set(['/data-dashboard'])
const BasemapSwitcher = lazy(() => import('@/components/common/BasemapSwitcher'))

const isFullBleedContentPath = (pathname: string) => {
  if (FULL_BLEED_CONTENT_PATHS.has(pathname)) {
    return true
  }

  return ['/disease/', '/patrol/', '/draw/'].some((prefix) => pathname.startsWith(prefix))
}

/**
 * 包含可交互 ArcGIS 地图的页面路径。这些页面必须禁用 layout 的 transform: scale 包裹，
 * 否则 ArcGIS 的鼠标命中检测（hitTest）会因为 clientX/clientY 与内部 DOM 尺寸不一致而漂移。
 * 注意：/data-dashboard 的地图仅作背景展示、无巡检等交互命中场景，不在此列，可正常缩放
 */
const MAP_PATH_KEYWORDS = ['/patrol', '/disease', '/draw', '/inspection', '/disease-scan', '/road-canvas', '/map']

const isMapContainingPath = (pathname: string) => {
  if (pathname === '/') return true
  return MAP_PATH_KEYWORDS.some((keyword) => pathname.includes(keyword))
}

/** 六边形 SVG Logo */
const HexagonLogo = () => (
  <div className={styles.hexagonWrap}>
    <svg viewBox="0 0 40 40" width="36" height="36">
      <defs>
        <linearGradient id="hex-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#6366f1" />
        </linearGradient>
      </defs>
      <polygon
        points="20,2 36,11 36,29 20,38 4,29 4,11"
        fill="none"
        stroke="url(#hex-grad)"
        strokeWidth="2"
      />
      <polygon
        points="20,8 30,14 30,26 20,32 10,26 10,14"
        fill="url(#hex-grad)"
        opacity="0.3"
      />
    </svg>
  </div>
)

const TechLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth()
  const { t } = useTranslation('common')
  const { currentLanguage, isEnUS } = useLanguage()
  const { isTech, isBlue } = useTheme()
  const basemapSwitcherVisible = useMapUiStore((state) => state.basemapSwitcherVisible)
  const navigate = useNavigate()
  const location = useLocation()
  const userDisplayName = getLocalizedUserDisplayName(user, currentLanguage)
  const isFullBleed = isFullBleedContentPath(location.pathname)
  const useContentPadding = !isFullBleed
  const showVersionDock = !VERSION_DOCK_HIDDEN_PATHS.has(location.pathname)
  const isMapPage = isMapContainingPath(location.pathname)
  const { scale } = useUiScale()
  // 地图页禁用整体容器缩放以保护 ArcGIS 命中检测，改由悬浮卡片自行叠加缩放（见 --app-panel-scale）
  const isScaleActive = scale !== 1 && !isMapPage
  const effectiveScale = isScaleActive ? scale : 1
  const panelSelfScale = isMapPage ? scale : 1

  const scaleVars = {
    '--app-content-scale': String(effectiveScale),
    '--app-content-scale-inverse': String(1 / effectiveScale),
  } as React.CSSProperties

  useEffect(() => {
    document.documentElement.style.setProperty('--app-content-scale', String(effectiveScale))
    document.documentElement.style.setProperty('--app-content-scale-inverse', String(1 / effectiveScale))
    document.documentElement.style.setProperty(PANEL_SELF_SCALE_CSS_VAR, String(panelSelfScale))
  }, [effectiveScale, panelSelfScale])

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <PoweroffOutlined />,
      label: t('action.logout'),
      onClick: logout,
    },
  ]

  return (
    <Layout className={styles.layout}>
      {/* 网格背景 + 扫描线（仅 tech 主题） */}
      {isTech && <div className={styles.gridBackground} />}
      {isTech && <div className={styles.scanline} />}
      {/* 微妙网格背景（仅 blue 主题） */}
      {isBlue && <div className={styles.blueGridBackground} />}

      <Header className={`${styles.header} ${isEnUS ? styles.headerCompact : ''}`}>
        {/* 左侧：六边形 Logo */}
        <div className={`${styles.logoSection} ${isEnUS ? styles.logoSectionCompact : ''}`} onClick={() => navigate('/')}>
          <HexagonLogo />
          <div className={styles.logoTextGroup}>
            <span className={`${styles.logoText} ${isEnUS ? styles.logoTextCompact : ''}`}>{t('header.appTitle')}</span>
          </div>
        </div>

        {/* 中间：图标导航 */}
        <div className={`${styles.menuContainer} ${isEnUS ? styles.menuContainerCompact : ''}`}>
          <TechTopMenu />
        </div>

        {/* 右侧：工具图标 + 用户信息 */}
        <div className={`${styles.rightSection} ${isEnUS ? styles.rightSectionCompact : ''}`}>
          <div className={`${styles.toolGroup} ${isEnUS ? styles.toolGroupCompact : ''}`}>
            {basemapSwitcherVisible && (
              <Suspense fallback={null}>
                <BasemapSwitcher variant="toolbar" className={styles.toolIconButton} />
              </Suspense>
            )}
            <UiScaleControl variant="toolbar" className={styles.toolIconButton} />
            <ThemeSwitcher variant="toolbar" className={styles.toolIconButton} />
            <LanguageSwitcher variant="toolbar" className={styles.toolIconButton} />
            <FullscreenToggle variant="toolbar" className={styles.toolIconButton} />
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" overlayStyle={{ minWidth: 140, zIndex: 1400 }} trigger={['click']}>
            <button
              type="button"
              className={`${styles.accountTrigger} ${isEnUS ? styles.accountTriggerCompact : ''}`}
              aria-label={t('header.userMenu')}
            >
              <span className={`${styles.userNameText} ${isEnUS ? styles.userNameTextCompact : ''}`} title={userDisplayName}>{userDisplayName}</span>
              <DownOutlined className={styles.accountArrow} aria-hidden="true" />
            </button>
          </Dropdown>
        </div>
      </Header>

      <Content className={styles.content}>
        <div className={styles.contentScaleViewport}>
          <div
            className={`${styles.contentScaleStage} ${isScaleActive ? styles.contentScaleStageActive : ''}`}
            style={scaleVars}
          >
            <div className={styles.contentScaleShell}>
              <div className={styles.contentCanvas}>
                {location.pathname !== '/' && <BreadcrumbNav />}
                <div
                  className={`${styles.contentInner} ${useContentPadding ? styles.withPadding : ''}`}
                  data-app-scroll-container="true"
                >
                  {children || <Outlet />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Content>

      {showVersionDock && (
        <div className={styles.versionDock} aria-label={`Application version ${APP_VERSION}`}>
          <span className={styles.versionValue}>{APP_VERSION}</span>
        </div>
      )}
    </Layout>
  )
}

export default TechLayout
